import { useState, useEffect, useRef } from 'preact/hooks'
import { h, render, VNode } from 'preact'
import '@/components/helpers/gm-state.types'

const STORAGE_KEY = 'image-view-preference'
const IMAGE_VIEW_CHANGED_EVENT = 'image-view-preference:changed'

function getGridView(el: HTMLElement | null): HTMLElement | null {
  const fromClosest = el?.closest?.('[data-large-image]')
  if (fromClosest) return fromClosest as HTMLElement
  return document.querySelector<HTMLElement>('[data-large-image]')
}

function getInitialPreference(): string {
  const isCustomer = typeof window !== 'undefined' && window.globals?.customer?.id
  if (isCustomer) {
    const preference = window.globals?.customer?.showLargeImages
    return preference ? 'true' : ''
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'true' ? 'true' : ''
  } catch {
    return ''
  }
}

interface ImageViewToggleButtonProps {
  className?: string
}

export function ImageViewToggleButton({ className = '' }: ImageViewToggleButtonProps): VNode<any> {
  const rootRef = useRef<HTMLElement | null>(null)
  const [largeImage, setLargeImage] = useState('')

  useEffect(() => {
    const grid = getGridView(rootRef.current)
    if (!grid) return
    const initial = getInitialPreference()
    grid.setAttribute('data-large-image', initial)
    setLargeImage(initial)

    const handleSync = (e: CustomEvent<{ value: string }>) => {
      if (e.detail?.value !== undefined) setLargeImage(e.detail.value)
    }
    window.addEventListener(IMAGE_VIEW_CHANGED_EVENT, handleSync as EventListener)
    return () => window.removeEventListener(IMAGE_VIEW_CHANGED_EVENT, handleSync as EventListener)
  }, [])

  const handleToggle = () => {
    const grid = getGridView(rootRef.current)
    if (!grid) return
    const next = largeImage === 'true' ? '' : 'true'
    grid.setAttribute('data-large-image', next)
    setLargeImage(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent(IMAGE_VIEW_CHANGED_EVENT, { detail: { value: next } }))

    const apiUrl = typeof window !== 'undefined' && window.globals?.apis?.bcApp?.proxyUrl
    const customerId = typeof window !== 'undefined' && window.globals?.customer?.id
    if (apiUrl && customerId) {
      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          namespace: window.globals?.apis?.bcApp?.largeImagePreference?.namespace,
          key: window.globals?.apis?.bcApp?.largeImagePreference?.key,
          value: next === 'true'
        }),
        credentials: 'same-origin'
      }).catch(() => {
        /* fire-and-forget */
      })
    }
  }

  const isLarge = largeImage === 'true'

  return h(
    'button',
    {
      ref: rootRef as any,
      type: 'button',
      className,
      onClick: handleToggle,
      'aria-pressed': isLarge,
      'aria-label': isLarge ? 'Use default image size' : 'Use large images'
    },
    [
      h(
        'div',
        { class: 'flex w-[37px] h-[20px] children:size-full' },
        h(
          'svg',
          {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '37',
            height: '20',
            viewBox: '0 0 37 20',
            fill: 'none'
          },
          [
            h('rect', {
              width: '36.6667',
              height: '20',
              rx: '10',
              fill: isLarge ? '#0E0E0E' : '#E0E0E0',
              class: 'transition-all'
            }),
            h('circle', {
              cx: isLarge ? '26.6693' : '9.9974',
              cy: '10.0001',
              r: '8.33333',
              fill: '#FFFFFF',
              class: 'transition-all'
            })
          ]
        )
      ),
      h('span', null, 'Large Images')
    ]
  )
}

export class ImageViewToggleButtonElement extends HTMLElement {
  connectedCallback(): void {
    render(
      h(ImageViewToggleButton, {
        className: this.getAttribute('data-class') || ''
      }),
      this
    )
  }
}
